import { Prisma } from "@prisma/client";
import { simpleActivityValues } from "./activity.types";
import { eventSummaryTypeValues } from "./events.types";
import { structureDocumentTypeValues } from "./structure-document.types";
import { UserSharedProtected } from "./user.types";
import { Paginated, Sortable } from "./utils.types";

export const getCustomStructureAppearanceTypeValues =
  Prisma.validator<Prisma.CustomStructureAppearanceDefaultArgs>()({
    include: {
      backgroundPicture: {
        select: {
          key: true,
          type: true,
          name: true,
        },
      },
    },
  });

export type CustomStructureAppearanceType = Prisma.CustomStructureAppearanceGetPayload<
  typeof getCustomStructureAppearanceTypeValues
>;

const baseStructureTypeValues = Prisma.validator<Prisma.StructureDefaultArgs>()({
  select: {
    id: true,
    businessName: true,
    businessSectorId: true,
    businessSectorRelation: {
      select: {
        id: true,
        name: true,
        parentId: true,
      },
    },
    businessEmail: true,
    phoneNumber: true,
    siret: true,
    siren: true,
    apeCode: true,
    vat: true,
    acceptedTerms: true,
    description: true,
    adminId: true,
    picture: {
      select: {
        key: true,
        type: true,
        name: true,
      },
    },
    bannerPicture: true,
    admin: {
      select: {
        firstName: true,
        lastName: true,
      },
    },
    address: {
      select: {
        address: true,
        city: true,
        zipCode: true,
        name: true,
        country: true,
        lat: true,
        long: true,
      },
    },
    socials: {
      select: {
        websiteUrl: true,
        linkedinUrl: true,
        facebookUrl: true,
        twitterUrl: true,
        instagramUrl: true,
        youtubeUrl: true,
      },
    },
    scannedStructures: {
      select: {
        userId: true,
        createdAt: true,
        eventId: true,
        event: {
          select: {
            id: true,
            name: true,
          },
        },
      },
    },
    likes: {
      select: {
        userId: true,
      },
    },
    activities: simpleActivityValues,
    products: {
      select: {
        id: true,
        name: true,
        description: true,
        link: true,
        images: {
          select: {
            file: {
              select: {
                key: true,
                type: true,
                name: true,
              },
            },
          },
        },
        events: {
          select: {
            id: true,
          },
        },
      },
    },
    documents: structureDocumentTypeValues,
    customStructureAppearance: getCustomStructureAppearanceTypeValues,
  },
} as const);

const commercialsSelect = (organiserId?: string) =>
  ({
    select: {
      id: true,
      firstName: true,
      lastName: true,
      email: true,
      commercialRole: true,
      user: {
        select: {
          id: true,
          firstName: true,
          lastName: true,
          email: true,
          roles: true,
          phoneNumber: true,
          picture: {
            select: {
              key: true,
              type: true,
              name: true,
            },
          },
        },
      },
      events: {
        where: {
          organiserId,
        },
        select: {
          name: true,
          dates: {
            include: {
              schedules: {
                include: {
                  timetables: {
                    orderBy: {
                      day: "asc" as const,
                    },
                  },
                },
                orderBy: {
                  fromDate: "asc" as const,
                },
              },
              exceptionalClosingDates: {
                orderBy: {
                  date: "asc" as const,
                },
              },
            },
          },
        },
      },
    },
  }) as const;

export const getStructureTypeValues = (organiserId?: string) =>
  Prisma.validator<Prisma.StructureDefaultArgs>()({
    select: {
      ...baseStructureTypeValues.select,
      eventStructures: {
        select: {
          event: eventSummaryTypeValues,
          exhibitorProfilesPack: true,
        },
      },
      commercials: commercialsSelect(organiserId),
      customStructureAppearance: getCustomStructureAppearanceTypeValues,
      slug: true,
    },
  });

export type StructureType = Prisma.StructureGetPayload<ReturnType<typeof getStructureTypeValues>>;

export const getStructureTypeValuesForEvent = () =>
  Prisma.validator<Prisma.StructureDefaultArgs>()({
    select: {
      ...baseStructureTypeValues.select,
      commercials: commercialsSelect(),
      customStructureAppearance: getCustomStructureAppearanceTypeValues,
      slug: true,
    },
  });

export type StructureTypeForEvent = Prisma.StructureGetPayload<
  ReturnType<typeof getStructureTypeValuesForEvent>
>;

export const defaultCustomStructureAppearance: CustomStructureAppearanceType = {
  backgroundColor: "white",
  filterOpacity: 0,
  backgroundPicture: null,
  id: "",
  structureId: "",
  backgroundPictureId: "",
};

export type AdditionalStructureFields = {
  scan: { createdAt: Date; event: { id: string; name: string } } | null;
  isLiked: boolean;
  isScanned: boolean;
};

export type StructureTypeFormatted = StructureType & AdditionalStructureFields;

const baseSelect = baseStructureTypeValues.select;

export const minimalStructureValues = Prisma.validator<Prisma.StructureDefaultArgs>()({
  select: {
    id: true,
    businessName: true,
    businessSectorRelation: baseSelect.businessSectorRelation,
    picture: baseSelect.picture,
    likes: baseSelect.likes,
    scannedStructures: baseSelect.scannedStructures,
    eventStructures: {
      select: {
        event: {
          select: {
            id: true,
            name: true,
          },
        },
      },
    },
  },
});

export type MinimalStructureForList = Prisma.StructureGetPayload<typeof minimalStructureValues>;

export type MinimalStructureFormattedForList = Omit<
  MinimalStructureForList,
  "likes" | "scannedStructures"
> &
  AdditionalStructureFields;

export const simpleStructureValues = Prisma.validator<Prisma.StructureDefaultArgs>()({
  include: {
    picture: true,
    address: true,
    socials: true,
  },
});

export type SimpleStructure = Prisma.StructureGetPayload<ReturnType<typeof getStructureTypeValues>>;

export enum StatusRequest {
  pending = "En attente",
  accepted = "Accepté",
  refused = "Refusé",
}

export const requestParticipationTypeValues =
  Prisma.validator<Prisma.ParticipationRequestDefaultArgs>()({
    include: {
      structure: true,
      event: {
        include: {
          organiser: true,
          dates: {
            include: {
              schedules: {
                include: {
                  timetables: {
                    orderBy: {
                      day: "asc",
                    },
                  },
                },
                orderBy: {
                  fromDate: "asc",
                },
              },
              exceptionalClosingDates: {
                orderBy: {
                  date: "asc",
                },
              },
            },
          },
          eventPlace: {
            include: {
              place: true,
            },
          },
          image: true,
        },
      },
    },
    select: {
      status: true,
      participation_request_id: true,
    },
  });

export type ParticipationRequestType = Prisma.ParticipationRequestGetPayload<
  typeof requestParticipationTypeValues
>;

const structureProductTypeValues = Prisma.validator<Prisma.StructureProductDefaultArgs>()({
  include: {
    images: {
      select: {
        file: {
          select: {
            key: true,
            type: true,
            name: true,
          },
        },
      },
    },
  },
});

export type StructureProductType = Prisma.StructureProductGetPayload<
  typeof structureProductTypeValues
>;

const userPicture = Prisma.validator<Prisma.UserDefaultArgs>()({
  select: {
    picture: true,
  },
});

export type ClientType = "structure" | "visitor";

export type Client = UserSharedProtected<
  {
    id: string;
    firstName: string;
    lastName: string;
    createdAt: Date;
    eventName: string;
    address: string | null;
    phone: string | null;
    ageGroup: string;
    email: string;
    interest: string;
    clientType: ClientType;
  } & Prisma.UserGetPayload<typeof userPicture>
>;

export type ScannedClientByCommercial = Client & {
  connectId: string;
};

export type FilterClientType = {
  searchText?: string;
  sortBy?: string;
  ageGroup?: string;
  clientType?: ClientType;
};

export const publicStructureValues = Prisma.validator<Prisma.StructureDefaultArgs>()({
  select: {
    id: true,
    picture: true,
    bannerPicture: true,
    adminId: true,
    socials: true,
    businessName: true,
    businessSectorRelation: {
      select: {
        id: true,
        name: true,
        parentId: true,
      },
    },
    address: true,
    acceptedTerms: true,
    phoneNumber: true,
    businessEmail: true,
    description: true,
    activities: {
      ...simpleActivityValues,
    },
    products: {
      include: {
        images: {
          include: {
            file: {
              select: {
                key: true,
                type: true,
                name: true,
              },
            },
          },
        },
        events: true,
      },
    },
    likes: true,
    scannedStructures: {
      select: {
        id: true,
        createdAt: true,
        updatedAt: true,
        eventId: true,
        structureId: true,
        userId: true,
      },
    },
    eventStructures: {
      select: {
        event: {
          select: {
            id: true,
            name: true,
            status: true,
            isPrivate: true,
            isPaidEntry: true,
            isIndoor: true,
            isOutdoor: true,
            dates: {
              include: {
                schedules: {
                  include: {
                    timetables: {
                      orderBy: {
                        day: "asc",
                      },
                    },
                  },
                  orderBy: {
                    fromDate: "asc",
                  },
                },
                exceptionalClosingDates: {
                  orderBy: {
                    date: "asc",
                  },
                },
              },
            },
            image: {
              select: {
                key: true,
                type: true,
              },
            },
            address: {
              select: {
                name: true,
              },
            },
            eventPlace: {
              select: {
                place: {
                  select: {
                    placeEntrances: {
                      select: {
                        address: {
                          select: {
                            name: true,
                          },
                        },
                      },
                      take: 1,
                    },
                  },
                },
              },
            },
            ticketTypes: {
              select: {
                id: true,
              },
            },
            badgeTypes: {
              select: {
                id: true,
              },
            },
          },
        },
      },
    },
    admin: {
      select: {
        firstName: true,
        lastName: true,
      },
    },
    bankDetails: true,
    commercials: true,
    customStructureAppearance: {
      include: {
        backgroundPicture: true,
      },
    },
    slug: true,
  },
});

export type PublicStructureType = Prisma.StructureGetPayload<typeof publicStructureValues> & {
  isLiked: boolean;
};

export const minimalEventForParticipationDates = Prisma.validator<Prisma.EventDateDefaultArgs>()({
  select: {
    isContinuous: true,
    schedules: {
      select: {
        fromDate: true,
        toDate: true,
        timetables: {
          select: {
            day: true,
            state: true,
            openingTime: true,
            closingTime: true,
            breakStartTime: true,
            breakEndTime: true,
          },
          orderBy: {
            day: "asc",
          },
        },
      },
      orderBy: {
        fromDate: "asc",
      },
    },
    exceptionalClosingDates: {
      select: {
        date: true,
      },
      orderBy: {
        date: "asc",
      },
    },
  },
});

export const minimalEventForParticipation = Prisma.validator<Prisma.EventDefaultArgs>()({
  select: {
    id: true,
    slug: true,
    name: true,
    isPaidEntry: true,
    isIndoor: true,
    isOutdoor: true,
    isPrivate: true,
    image: {
      select: {
        key: true,
        type: true,
      },
    },
    address: {
      select: {
        name: true,
        city: true,
      },
    },
    eventPlace: {
      select: {
        place: {
          select: {
            placeEntrances: {
              take: 1,
              select: {
                address: {
                  select: {
                    name: true,
                  },
                },
              },
            },
          },
        },
      },
    },
    dates: minimalEventForParticipationDates,
    ticketTypes: {
      select: {
        id: true,
      },
    },
    badgeTypes: {
      select: {
        id: true,
      },
    },
    ticketing: {
      select: {
        availabilityStartingDate: true,
        availabilityEndingDate: true,
      },
    },
    promotionDates: true,
  },
});

export type MinimalEventForParticipation = Prisma.EventGetPayload<
  typeof minimalEventForParticipation
> & {
  isTicketingAvailable: boolean;
  hasTicketsOrBadges: boolean;
  isSponsored: boolean;
};

export const minimalProductForExponent = Prisma.validator<Prisma.StructureProductDefaultArgs>()({
  select: {
    id: true,
    name: true,
    description: true,
    link: true,
    images: {
      select: {
        file: {
          select: {
            key: true,
            type: true,
            name: true,
          },
        },
      },
    },
    events: {
      select: {
        id: true,
      },
    },
  },
});

export type MinimalProductForExponent = Prisma.StructureProductGetPayload<
  typeof minimalProductForExponent
>;

export const exponentStructureValues = Prisma.validator<Prisma.StructureDefaultArgs>()({
  select: {
    id: true,
    businessName: true,
    businessSectorRelation: {
      select: {
        id: true,
        name: true,
        parentId: true,
      },
    },
    businessEmail: true,
    phoneNumber: true,
    acceptedTerms: true,
    description: true,
    adminId: true,
    picture: {
      select: {
        key: true,
        type: true,
        name: true,
      },
    },
    bannerPicture: true,
    address: {
      select: {
        address: true,
        city: true,
        zipCode: true,
        name: true,
        country: true,
        lat: true,
        long: true,
      },
    },
    socials: {
      select: {
        websiteUrl: true,
        linkedinUrl: true,
        facebookUrl: true,
        twitterUrl: true,
        instagramUrl: true,
        youtubeUrl: true,
      },
    },
    likes: {
      select: {
        userId: true,
      },
    },
    activities: simpleActivityValues,
    products: minimalProductForExponent,
    documents: structureDocumentTypeValues,
    customStructureAppearance: getCustomStructureAppearanceTypeValues,
    commercials: {
      where: { visibleOnExhibitorPage: true },
      select: {
        id: true,
        firstName: true,
        lastName: true,
        email: true,
        jobTitle: true,
        visibleOnExhibitorPage: true,
        user: {
          select: {
            firstName: true,
            lastName: true,
            email: true,
            phoneNumber: true,
          },
        },
        events: {
          select: {
            id: true,
          },
        },
      },
    },
    slug: true,
  },
});

export type ExponentStructureType = Prisma.StructureGetPayload<typeof exponentStructureValues> & {
  isLiked: boolean;
  participations: MinimalEventForParticipation[];
};

export const exhibitorDirectoryItemValues =
  Prisma.validator<Prisma.StructureDefaultArgs>()({
    select: {
      id: true,
      adminId: true,
      businessName: true,
      businessEmail: true,
      phoneNumber: true,
      picture: {
        select: {
          key: true,
          type: true,
          name: true,
        },
      },
      businessSectorRelation: {
        select: {
          id: true,
          name: true,
        },
      },
      address: {
        select: {
          address: true,
          city: true,
          zipCode: true,
          name: true,
          lat: true,
          long: true,
        },
      },
      products: {
        select: {
          name: true,
        },
      },
    },
  });

export type ExhibitorDirectoryItem = Prisma.StructureGetPayload<
  typeof exhibitorDirectoryItemValues
>;

export type MapBounds = {
  southWestLat: number;
  southWestLng: number;
  northEastLat: number;
  northEastLng: number;
};

export type ExhibitorDirectoryFilters = Partial<Sortable<Paginated>> & {
  searchText?: string;
  businessSectorIds?: string[];
  bounds?: MapBounds;
};
